import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BrandingText from "../../assets/Branding/BrandingText";
import { TextBody } from "../../components/DesignSystem/DesignSystemText";
import Modal from "../../components/Modal/Modal";
import { useDiscordAuth } from "../../hooks/useDiscordAuth";
import { getUserIdentity, IKudosDetails } from "../../services/apis";
import { postDiscordAuthCode } from "../../services/apis/AccountApi/Discord";
import { openDiscordAuthWindow } from "../../services/auth/discord";
import { useWallet } from "../../services/wallet/useWallet";
import AccountModalBase from "../Account/AccountModalBase";
import AccountModalContainer from "../Account/AccountModalContainer";
import { useLoginScreen } from "../Account/LoginScreenState";
import { isUserAllowlisted } from "../CreateKudos/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  finalAction: () => void;
  kudosDetails: IKudosDetails;
  type: "discord" | "create" | "claim";
}

const MultiStepAuthModal = ({
  open,
  onClose,
  finalAction,
  kudosDetails,
  type,
}: Props) => {
  const {
    isLoggedIn,
    username,
    discordAccounts,
    setDiscordAccounts,
    addresses,
    provider,
    changeNetwork,
  } = useWallet();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const loginScreen = useLoginScreen();

  const isAllowlistRequired =
    kudosDetails?.claimabilityAttributes?.isAllowlistRequired;

  const { discordAuthCode, discordError } = useDiscordAuth();
  const isDiscord = type === "discord";
  const isCreate = type === "create";

  const claimStep = {
    text: provider === "wagmi" ? "Sign to claim" : "Claim",
  };

  const createStep = {
    text: provider === "wagmi" ? "Sign to send" : "Send",
  };

  useEffect(() => {
    const checkDiscordAuth = async () => {
      if (!!discordError) {
        toast.error(discordError);
        onClose();
      }
    };
    checkDiscordAuth();
  }, [discordError]);

  const hasNewDiscordAuth = !!discordAuthCode;

  const delay = (time: number = 1000) =>
    new Promise((resolve) => setTimeout(resolve, time));

  const steps = useMemo(() => {
    if (isDiscord) {
      return [loginStep, discordStep, claimStep];
    } else if (isCreate) {
      return [loginStep, createStep];
    } else {
      return [loginStep, claimStep];
    }
  }, [isDiscord]);

  useEffect(() => {
    const checkDiscordAllowlist = async () => {
      try {
        if (isDiscord && hasNewDiscordAuth) {
          await postDiscordAuthCode(discordAuthCode);

          const identity = await getUserIdentity(username);
          setDiscordAccounts(identity.discordAccounts ?? []);

          if (
            !isAllowlistRequired ||
            (await isUserAllowlisted(kudosDetails.tokenId.toString(), {
              addresses,
              discordAccounts: identity.discordAccounts,
            }))
          ) {
            setCurrentStepIndex(2);
          } else {
            toast.error("Your Discord account is not allowlisted");
            onClose();
          }
        }
      } catch (error: any) {
        toast.error(error.message || error);
      }
    };
    checkDiscordAllowlist();
  }, [hasNewDiscordAuth]);

  useEffect(() => {
    const checkState = async () => {
      try {
        if (!open) {
          return;
        }

        if (!isLoggedIn) {
          loginScreen.open();
          return;
        }

        if (currentStepIndex === 0 && isLoggedIn) {
          if (provider === "wagmi") {
            await changeNetwork();
          }
          setCurrentStepIndex(1);
        } else if (isDiscord && currentStepIndex === 1) {
          if (discordAccounts.length && !hasNewDiscordAuth) {
            setCurrentStepIndex(2);
          } else {
            await delay();
            openDiscordAuthWindow();
          }
        } else if (currentStepIndex === steps.length - 1) {
          await delay();
          if (
            type === "claim" &&
            isAllowlistRequired &&
            !(await isUserAllowlisted(kudosDetails.tokenId.toString(), {
              addresses,
            }))
          ) {
            return;
          } else {
            await finalAction();
          }
        }
      } catch (error: any) {
        onClose();
        toast.error(error.message);
      }
    };
    checkState();
  }, [isLoggedIn, currentStepIndex, open]);

  const close = () => {
    if (!loginScreen.isOpen) {
      setCurrentStepIndex(0);
      onClose();
    }
  };

  useEffect(() => {
    if (!loginScreen.isOpen && !isLoggedIn) {
      close();
    }
  }, [loginScreen.isOpen]);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        className={loginScreen.isOpen ? "hidden" : ""}
      >
        <AccountModalBase
          title={isCreate ? "Send kudos" : "Claim kudos"}
          helperText="Get help"
          helperLink={`${BrandingText.DiscordInviteUrl}`}
          onClose={close}
        >
          <div className="flex flex-col gap-y-[10rem] mb-6 mt-7">
            {(steps || []).map((step: any, index: number) => {
              return (
                <AccountModalContainer
                  key={`multi-step-${index}`}
                  loading={currentStepIndex === index}
                  complete={currentStepIndex > index}
                >
                  <Number value={index + 1} />
                  <TextBody>{step?.text}</TextBody>
                </AccountModalContainer>
              );
            })}
          </div>
        </AccountModalBase>
      </Modal>
    </>
  );
};

const loginStep = {
  text: "Sign to log in",
};

const discordStep = {
  text: "Connect Discord",
};

const Number = ({ value }: { value: number }) => {
  return (
    <div className="rounded-[50%] h-8 w-8 !bg-[#DFDFDF] flex justify-center items-center">
      <TextBody className="!font-bold">{value}</TextBody>
    </div>
  );
};

export default MultiStepAuthModal;
